import { FC, ReactElement, ReactNode, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import { TimeFilterSkeleton } from './Skeleton';
import { SportTitleSkeleton } from 'newelements/Sportsbook/Esport/Mobile/__partials/Sports/__partials/SportTitle/Skeleton';
import { HeaderPickerSkeleton } from 'components/Shared/MobileSkeleton/HeaderPickerSkeleton';
import { Viewport } from 'components/Viewport';
import { setTimeFilterData } from 'store/actions';

const { TimeFiltersMobile } = lazy(
  () => import(/* webpackChunkName: "timefilter-mobile" */ './Mobile')
);

const { TimeFilterDesktop } = lazy(
  () => import(/* webpackChunkName: "timefilter-desktop" */ './Desktop')
);

type TTimeFilterProps = {
  title?: string | ReactElement | ReactNode;
  isCalendar?: boolean;
  withoutDays?: boolean;
  noTitle?: boolean;
  resetTimeFilterMode?: number[];
  isDisabled?: boolean;
  skeleton?: {
    withCount?: boolean;
    useAlternative?: boolean;
  };
  showSortButton?: boolean;
};

export const TimeFilter: FC<TTimeFilterProps> = ({
  title,
  isCalendar,
  withoutDays,
  noTitle,
  resetTimeFilterMode,
  isDisabled,
  skeleton,
  showSortButton
}) => {
  const dispatch = useDispatch();
  const dateOptions = useMemo(() => {
    // eslint-disable-next-line no-magic-numbers
    const days = isCalendar ? [0, 1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5, 6, 7];

    return days.map(value => {
      const date = new Date();
      date.setDate(date.getDate() + value);

      return {
        value: dayjs(date.valueOf()).format(SpringConfigs.DT.shortDate),
        count: value,
        name: dayjs(date.valueOf()).format(SpringConfigs.DT.shortDate)
      };
    });
  }, [isCalendar]);

  useEffect(() => {
    return () => {
      if (
        SpringConfigs.PAGE_URLS.esport &&
        window.location.href.includes(SpringConfigs.PAGE_URLS.esport)
      ) {
        dispatch(
          setTimeFilterData({
            option: 'All',
            date: false,
            count: 0,
            name: 'All Events'
          })
        );
      }
    };
  }, []);

  return (
    <Viewport
      desktop={TimeFilterDesktop}
      mobile={TimeFiltersMobile}
      fallback={
        skeleton?.useAlternative ? (
          <div className="sportsHeader-titleInner-skeleton">
            <SportTitleSkeleton />
            <HeaderPickerSkeleton noTitle />
          </div>
        ) : (
          <TimeFilterSkeleton
            noTitle={noTitle}
            withCount={skeleton?.withCount}
          />
        )
      }
      innerProps={{
        title,
        isDisabled,
        dateOptions,
        showSortButton,
        ...(isMobile()
          ? {
              resetTimeFilterMode
            }
          : {
              withoutDays,
              noTitle
            })
      }}
    />
  );
};
